import axios from 'axios';
import globals from "../Globals";
import EventBus from '../EventBus';

export default class requests {
    static toBase64(buf) {
        return btoa(String.fromCharCode(...new Uint8Array(buf)))
    }

    static fromBase64(b64) {
        const binary = atob(b64)
        const buf = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i)
        return buf.buffer
    }

    static async importKeys() {
        globals.privateKey = await crypto.subtle.importKey("pkcs8",
            this.fromBase64(globals.loginData.privateKey),
            { name: "RSA-PSS", hash: "SHA-512" },
            false, ["sign"]);

        globals.privateKeyDecrypt = await crypto.subtle.importKey("pkcs8",
            this.fromBase64(globals.loginData.privateKey),
            { name: "RSA-OAEP", hash: "SHA-512" },
            false, ["decrypt"]);

        globals.backendPublicKey = await crypto.subtle.importKey("spki",
            this.fromBase64(globals.backendPublicKeyRaw),
            { name: "RSA-PSS", hash: "SHA-512" },
            false, ["verify"])

        globals.backendPublicKeyEncrypt = await crypto.subtle.importKey("spki",
            this.fromBase64(globals.backendPublicKeyRaw),
            { name: "RSA-OAEP", hash: "SHA-512" },
            false, ["encrypt"])
    }

    static async getKeys() {
        // Import keys
        if (globals.loginData.privateKey != null) {
            await this.importKeys();
            return;
        }

        // Generate new keys
        const keyPair = await crypto.subtle.generateKey({
            name: "RSA-PSS",
            modulusLength: 2048,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: "SHA-512"
        }, true, ["sign", "verify", "decrypt"]);

        // Export keys
        globals.loginData.privateKey = this.toBase64(await crypto.subtle.exportKey("pkcs8", keyPair.privateKey));
        globals.loginData.publicKey = this.toBase64(await crypto.subtle.exportKey("spki", keyPair.publicKey));
        globals.loginData.deviceId = null;

        localStorage.setItem("login", JSON.stringify(globals.loginData));
    }

    static async login(credential){
        await this.getKeys();
        let response;

        try {
            response = await axios.post(globals.serverUrl + globals.apiLogin, {
                credential: credential,
                deviceId: globals.loginData.deviceId,
                publicKey: globals.loginData.publicKey, // send only the public key
            });
        } catch (error) {
            return error.message;
        }

        if (response.data.ok) {
            // Decrypt response auth: deviceId, accessToken, tokenExpireDate
            const encryptedBytes = this.fromBase64(response.data.auth);
            let decrypted = await crypto.subtle.decrypt(
                { name: "RSA-OAEP" },
                globals.privateKeyDecrypt,
                encryptedBytes
            );
            let decoder = new TextDecoder();
            let authStr = decoder.decode(decrypted);
            let auth = authStr.split("\n");

            // Save login data
            globals.loginData.deviceId = auth[0];
            globals.loginData.accessToken = auth[1];
            globals.loginData.tokenExpireDate = auth[2];
            globals.loginData.name = response.data.name;
            globals.loginData.email = response.data.email;
            localStorage.setItem("login", JSON.stringify(globals.loginData));

            return auth[1] != null ? true : "New device: Ask another admin to authorize this device.";
        } else {
            return response.data.error;
        }
    }

    static logout(unregisterDevice) {
        this.request("logout", {"unregisterDevice": unregisterDevice});

        if (unregisterDevice) {
            localStorage.removeItem("login");
            globals.loginData = {};
        } else {
            globals.loginData.accessToken = null;
            globals.loginData.tokenExpireDate = null;
            localStorage.setItem("login", JSON.stringify(globals.loginData));
        }
    }

    static async request(action, args) {
        // Debug
        console.log("Request: ", action);
        console.log("Payload: ", JSON.stringify(args));

        // Generate command payload
        let cmdStr = globals.loginData.deviceId + "\n" + globals.loginData.accessToken + "\n" + Date.now() + "\n" + action;

        const encoder = new TextEncoder();
        let cmdBytes = encoder.encode(cmdStr);

        // Split cmdBytes into 126-byte chunks
        let cmdHeaders = {};
        const chunkSize = 126;
        for (let i = 0; i < cmdBytes.length; i += chunkSize) {
            let chunk = cmdBytes.slice(i, i + chunkSize);

            // Encrypt with backend public key
            const encrypted = await crypto.subtle.encrypt(
                { name: "RSA-OAEP" },
                globals.backendPublicKeyEncrypt,
                chunk
            );

            cmdHeaders[`X-Cmd${i / chunkSize}`]  = this.toBase64(encrypted);
        }

        // Generate request
        let requestData, file;
        if (args instanceof File) {
            requestData = args;
            file = true;
        } else {
            requestData = JSON.stringify(args);
            file = false;
        }

        // Generate signature
        const data = encoder.encode(cmdStr + (file ? "" : requestData));
        const signature = this.toBase64(await crypto.subtle.sign(
            {name: "RSA-PSS", saltLength: 64},
            globals.privateKey,
            data
        ));

        let headers = {
            "Content-Type": file ? "application/octet-stream" : "application/json",
            "X-Signature": signature,
            ...cmdHeaders
        };

        // Send request
        let response;
        try {
            response = await axios.post(globals.serverUrl + globals.apiEntryPoint, requestData, {
                headers: headers,
                responseType: 'text' // 'text' disables automatic JSON parsing
            });
        } catch (error) {
            throw new Error(error.response.data);
        }

        // Direct commands
        if (response.data === "refresh_login") {
            if (globals.loginData.accessToken != null) {
                EventBus.emit("logout", false);
            }
            throw new Error("Login Expired.");
        }

        // Verify backend signature
        const responseSignature = response.headers['x-signature'];
        const responseData = encoder.encode(response.data + signature);

        const isValid = await crypto.subtle.verify(
            {name: "RSA-PSS", saltLength: 64},
            globals.backendPublicKey,
            this.fromBase64(responseSignature),
            responseData
        );

        if (!isValid) {
            throw new Error("Invalid signature from server.");
        }

        // Return response body
        return JSON.parse(response.data);
    }
}